import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { DataRow, Table, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { useResponsive } from "capitalrx-components-2.0/lib/layout";

import { MedicarePrescriptionPaymentPlanEstimate } from "@/App/types/medicarePrescriptionPaymentPlan";
import { formatDollars } from "@/App/utils/utils";
import { MONTHS } from "./utils";

const HighlightedTable = styled(Table)`
  thead {
    tr {
      th.custom:nth-child(5) {
        background-color: ${(props) => props.theme.medicare_prescription_payment_plan_with_color}0D !important;

        &:hover {
          background-color: ${(props) => props.theme.medicare_prescription_payment_plan_with_color}0D !important;
        }
      }
    }
  }

  tbody {
    tr {
      td {
        background-color: white !important;
      }

      td.custom:nth-child(5) {
        background-color: ${(props) => props.theme.medicare_prescription_payment_plan_with_color}0D !important;
      }
    }
  }
`;

interface PaymentTableProps {
  readonly estimatedPayments: MedicarePrescriptionPaymentPlanEstimate[];
  readonly monthlyCost: number;
}

interface PaymentTableRow {
  readonly month: string;
  readonly with: number;
  readonly without: number;
  readonly expectedCost: number;
}

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculatorPaymentTable";

export const PaymentTable: FC<PaymentTableProps> = ({ estimatedPayments, monthlyCost }) => {
  const { t: translate } = useTranslation();

  const { isCompressed } = useResponsive({
    isCompressed: {
      desktop: false,
      mobile: true,
    },
  });

  const dataRows: DataRow<PaymentTableRow>[] = useMemo(() => {
    return estimatedPayments.map((payment) => {
      const month = MONTHS[new Date(payment.month).getUTCMonth() + 1];

      return {
        id: month,
        data: {
          month: month,
          with: payment.billed_amount,
          without: parseFloat(payment.true_out_of_pocket_amount),
          expectedCost: monthlyCost,
        },
      };
    });
  }, [estimatedPayments, monthlyCost]);

  const dataSource = useSimpleTableDataSource(dataRows);

  return (
    <HighlightedTable dataSource={dataSource}>
      <Table.Custom
        title={translate(`${TRANSLATION_PATH}.month`)}
        name="month"
        initialWidth="span"
      >
        {(value: string) => {
          const translatedMonth = translate(`${TRANSLATION_PATH}.${value.toLowerCase()}`);
          return <>{isCompressed ? translatedMonth.substring(0, 3) : translatedMonth}</>;
        }}
      </Table.Custom>
      <Table.Custom
        name="expectedCost"
        title={translate(`${TRANSLATION_PATH}.expectedMonthlyCost`)}
        sortable
        initialWidth="span"
      >
        {(value) => {
          return <>{formatDollars(value as number)}</>;
        }}
      </Table.Custom>
      <Table.Custom
        name="without"
        title={translate(`${TRANSLATION_PATH}.paymentWithoutMPPP`)}
        sortable
        initialWidth="span"
      >
        {(value) => {
          return <>{formatDollars(value as number)}</>;
        }}
      </Table.Custom>
      <Table.Custom
        name="with"
        title={isCompressed ? translate(`${TRANSLATION_PATH}.compressedBillEstimate`) : translate(`${TRANSLATION_PATH}.nonCompressedBillEstimate`)}
        sortable
        initialWidth="span"
      >
        {(value) => {
          return <>{value !== undefined ? formatDollars(value as number) : "--"}</>;
        }}
      </Table.Custom>
    </HighlightedTable>
  );
};
